import { SetsModal } from '@/components/SetsModal';
import { appTheme } from '@/config/colors';
import { useDashboardContext } from '@/hooks/useDashboardContext';
import { useWindowResize } from '@/hooks/useWindowResize';
import { useCallback, useMemo, useState } from 'react';
import { FlatList, Platform, StyleSheet, View } from 'react-native';

import { NoSets } from './NoSets';
import { Set } from './Set';

const styles = StyleSheet.create({
  full: {
    flex: 1,
  },
  addSetBtn: {
    marginHorizontal: -8,
  },
  addSetBtnContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  addSetBtnContentTxt: {
    marginLeft: 8,
    fontSize: 16,
    color: appTheme.primary,
  },
  rowWithNegativeMargin: {
    flexDirection: 'row',
    marginHorizontal: -4,
    gap: 8,
  },
});

export function DashboardSets({
  wordsets,
  sentencesets,
  textsets,
  refreshPatient,
}) {
  const { selectedID, fetchPatientsWrapper, selectedPatient, patientsSyncing } =
    useDashboardContext();
  const [isSetsModalVisible, setIsSetsModalVisible] = useState(false);
  const selectedPatientFullName = useMemo(
    () =>
      selectedPatient
        ? `${selectedPatient?.firstName} ${selectedPatient?.lastName}`
        : '',
    [selectedPatient],
  );
  const windowWidth = useWindowResize();

  const sets = [
    {
      title: 'Wörtersets',
      data: wordsets,
    },
    {
      title: 'Sätzesets',
      data: sentencesets,
    },
    {
      title: 'Textesets',
      data: textsets,
    },
  ];

  const noSetsAvailable = useMemo(() => {
    const setsLength = [wordsets.length, sentencesets.length, textsets.length];
    const areAllEmpty = setsLength.every((entry) => entry === 0);
    return areAllEmpty;
  }, [wordsets.length, sentencesets.length, textsets.length]);

  const changeSetsModalVisibility = useCallback((value) => {
    if (!value) {
      return setIsSetsModalVisible((prevState) => !prevState);
    }
    setIsSetsModalVisible(value);
  }, []);

  const setsRenderer = () => {
    if (windowWidth <= 950 && Platform.OS === 'web') {
      return (
        <FlatList
          keyExtractor={(item, index) => index}
          data={sets}
          style={styles.full}
          renderItem={({ item, index }) => {
            const type = item.title;
            const data = item.data;
            return (
              <View key={type} style={{ marginBottom: 32 }}>
                <Set
                  key={type + index}
                  patientsSyncing={patientsSyncing}
                  selectedPatientFullName={
                    selectedPatient
                      ? `${selectedPatient?.firstName} ${selectedPatient?.lastName}`
                      : ''
                  }
                  fetchPatientsWrapper={fetchPatientsWrapper}
                  patientID={selectedID}
                  type={type}
                  data={data}
                  index={index}
                  refreshPatient={refreshPatient}
                />
              </View>
            );
          }}
        />
      );
    }

    return sets.map((set, index) => {
      const type = set.title;
      const data = set.data;
      return (
        <Set
          patientsSyncing={patientsSyncing}
          selectedPatientFullName={selectedPatientFullName}
          fetchPatientsWrapper={fetchPatientsWrapper}
          patientID={selectedID}
          key={type + index}
          type={type}
          data={data}
          index={index}
          refreshPatient={refreshPatient}
        />
      );
    });
  };

  if (noSetsAvailable) {
    return <NoSets />;
  }

  return (
    <>
      <View style={styles.rowWithNegativeMargin}>{setsRenderer()}</View>
      <SetsModal
        isSetsModalVisible={isSetsModalVisible}
        changeSetsModalVisibility={changeSetsModalVisibility}
      />
    </>
  );
}
